<script setup>
/**
 * FormControlProductTypesFeatures
 * Dynamic configuration grid for managing multi-tier product features.
 * Allows administrators to add, order, and eliminate feature parameters
 * while maintaining strict data schemas via centralized warning confirmation vectors.
 */
import { computed, onMounted, getCurrentInstance, set as vueSet } from 'vue';
import Button from "../Button.vue";

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Synchronous Computeds ---

/**
 * Accesses and normalizes the target dataset matrix layout from active orchestrator properties.
 */
const items = computed(() => {
    if (!props.currentData || !props.field?.name) return [];
    return props.currentData[props.field.name] || [];
});

// --- Methods ---

/**
 * Appends a fresh, blank feature structure template block safely to the root data collection.
 */
const addFeatureField = () => {
    const freshFeatureItem = { name: '', options: '' };

    // Safety check: Initialize the root array proxy structure if it falls back to null/undefined
    if (!props.currentData[props.field.name]) {
        vueSet(props.currentData, props.field.name, [freshFeatureItem]);
    } else {
        // Mutate through target reference explicitly to enforce reactive layout updates
        props.currentData[props.field.name].push(freshFeatureItem);
    }
};

/**
 * Prompts the administrative session with a confirmation intercept modal,
 * safely slicing selected parameters out of the core data array upon acknowledgment.
 */
const removeFeatureField = (targetIndex) => {
    store.dispatch('modals/show', {
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Are you sure?',
            modalBody: `<p class="alert alert-warning m-0 small">
                Changes to a type’s features may affect existing products of that type.
                It is recommended to review all product features after making changes.
            </p>`,
            onConfirm: () => {
                if (props.currentData?.[props.field.name]) {
                    props.currentData[props.field.name].splice(targetIndex, 1);
                }
            }
        }
    });
};

// --- Lifecycle ---

onMounted(async () => {
    // Hide the label as it's just a duplicate of the fieldset's legend element container.
    if (props.field) {
        props.field.label = '';
    }

    // Seed systematic defaults down to state stores cleanly
    await store.dispatch(`${props.formId}/setDefaultValue`, {
        name: 'features',
        value: []
    });
});
</script>

<template>
    <div class="form-control-product-types-features-container">

        <div class="alert alert-warning py-2 small border-0 shadow-sm mb-3">
            Changes to a type’s features may affect existing products of that type.
            <br>
            It is recommended to review all product features after making changes.
        </div>

        <div
            v-for="(item, index) in items"
            :key="'feature-row-' + index"
            class="mb-3 list-group border shadow-sm rounded"
        >
            <div class="list-group-item small pe-0 d-flex align-items-start border-0">
                <div class="flex-fill me-2">
                    <input
                        v-model="item.name"
                        name="name"
                        placeholder="Feature name (e.g., Size, Material)"
                        class="mb-2 form-control form-control-sm fw-bold"
                        required
                        :autofocus="index === items.length - 1"
                    >

                    <input
                        v-model="item.options"
                        name="options"
                        placeholder="Comma-separated choices (e.g., Small, Medium, Large)"
                        class="form-control form-control-sm"
                    >
                    <div class="form-text text-muted mt-1 small ms-1">
                        <em>Enter a comma-delimited list of options E.g. Size, Colour etc.</em>
                    </div>
                </div>

                <Button
                    class="m-1 btn-sm p-1 lh-1 text-danger hover-opacity-1"
                    :default-class="'btn-link'"
                    @click.native="removeFeatureField(index)"
                >
                    <i class="bi bi-x-lg" />
                </Button>
            </div>
        </div>

        <Button
            class="btn-sm mt-1"
            @click.native="addFeatureField"
        >
            <i class="bi bi-plus-lg me-1" /> Add feature
        </Button>

    </div>
</template>

<style scoped>
/* Ensure contextual button actions don't distort alignment across line grids */
.hover-opacity-1 {
    opacity: 0.65;
    transition: opacity 0.15s ease-in-out;
}
.hover-opacity-1:hover {
    opacity: 1;
}
</style>